import { defineStore } from 'pinia';
import { trackAddToCart, contentId } from '@/analytics/track';

export const useCartStore = defineStore('cart', {
  state: () => ({
    cart: [],
    totalQty: 0,
    TotalOrderPrice: 0,
    TotalDiscount: 0,
    active: {
      id: 1,
      meal_name: '',
      meal_id:null,
      selected_meal_portion_id: null,
      selected_meal_portion_name: '',
      selected_meal_portion_weight: 0,
      selected_meal_portion_price: 0,
      selected_meal_addon_price: 0,
      selected_meal_portion_protein: 0,
      selected_meal_portion_carbs: 0,
      selected_meal_portion_fat: 0,
      selected_meal_portion_calories: 0,
      isMealSet: false,
      addons: [],
      quantity: 1,
      total_price: 0,
      discount: 0,
    },
    appliedCoupon:null,
  }),
  actions: {
    addToCart(item) {
      this.active.meal_name = item.name;
      this.active.meal_id = item.id;
      // Keep the dish's translation code so the cart can show the localised name.
      this.active.meal_trans_code = item.trans_code || item.trans_code_code || null;
      const newItem = { ...this.active };
      this.cart.push(newItem);
      this.updateTotal();
      this.resetActive();
      
      // First-party analytics + all configured marketing pixels (Meta/GA/TikTok)
      trackAddToCart({
        value: newItem.total_price || 0,
        items: [{ item_id: contentId({ kind: 'item', sku: newItem.sku, variant_id: newItem.selected_meal_portion_id, meal_id: newItem.meal_id }),
          item_name: item.name || newItem.meal_name, price: Number(newItem.selected_meal_portion_price || 0), quantity: newItem.quantity || 1 }],
        contentName: item.name || newItem.meal_name,
        meta: { name: item.name || newItem.meal_name, id: item.id || newItem.meal_id },
      });
    },
    resetActive() {
      this.active = {
        id: this.generateId(),
        meal_name: '',
        meal_id: null,
        selected_meal_portion_id: null,
        selected_meal_portion_name: '',
        selected_meal_portion_weight: 0,
        selected_meal_portion_price: 0,
        selected_meal_addon_price: 0,
        selected_meal_portion_protein: 0,
        selected_meal_portion_carbs: 0,
        selected_meal_portion_fat: 0,
        selected_meal_portion_calories: 0,
        isMealSet: false,
        addons: [],
        quantity: 1,
        total_price: 0,
        discount: 0,
      };
    },
    generateId() {
      return this.cart.length > 0 ? this.cart[this.cart.length - 1].id + 1 : 1;
    },
    updateTotal() {
      this.totalQty = this.cart.reduce((sum, item) => sum + item.quantity, 0);
      this.TotalOrderPrice = this.cart.reduce((sum, item) => sum + item.total_price, 0);
      this.calculateDiscount();
    },
    removeFromCart(id) {
      this.cart = this.cart.filter(item => item.id !== id);
      this.cart.forEach((item, index) => {
        item.id = index + 1;
      });
      this.updateTotal();
    },
    updateActivePortion(portion) {
      this.active.selected_meal_portion_id = portion.id;
      this.active.selected_meal_portion_name = portion.portion_name;
      this.active.selected_meal_portion_weight = portion.portion_weight;
      this.active.selected_meal_portion_price = parseFloat(portion.price);
      this.active.selected_meal_portion_protein = portion.protein;
      this.active.selected_meal_portion_carbs = portion.carbs;
      this.active.selected_meal_portion_fat = portion.fats;
      this.active.selected_meal_portion_calories = portion.calories;
      const codeMatch = portion.portion_code && String(portion.portion_code).toLowerCase() === "meal_set";
      const nameMatch = portion.portion_name && String(portion.portion_name).toLowerCase() === "meal set";
      this.active.isMealSet = codeMatch || nameMatch;
      this.active.addons = [];
      this.updateActiveTotalPrice();
    },
    updateActiveAddons() {
      const addonTotal = this.active.addons.reduce((sum, addon) => sum + parseFloat(addon.price), 0);
      this.active.selected_meal_addon_price = addonTotal;
      this.updateActiveTotalPrice();
    },
    updateActiveQuantity(quantity) {
      if (quantity < 1) {
        quantity = 1;
      }
      this.active.quantity = quantity;
      this.updateActiveTotalPrice();
    },
    updateActiveTotalPrice() {
      const itemPrice = parseFloat(this.active.selected_meal_portion_price);
      const addonPrice = parseFloat(this.active.selected_meal_addon_price)
      const totalPrice = (itemPrice + addonPrice) * this.active.quantity;
      this.active.total_price = totalPrice;
    },
    increaseItemQuantity(itemId) {
      const item = this.cart.find(item => item.id === itemId);
      if (item) {
        item.quantity += 1;
        item.total_price = item.quantity * (parseFloat(item.selected_meal_portion_price) + parseFloat(item.selected_meal_addon_price || 0));
        this.updateTotal();
      }
    },
    decreaseItemQuantity(itemId) {
      const item = this.cart.find(item => item.id === itemId);
      if (item) {
        item.quantity -= 1;
        if (item.quantity <= 0) {
          this.removeFromCart(itemId);
        } else {
          item.total_price = item.quantity * (parseFloat(item.selected_meal_portion_price) + parseFloat(item.selected_meal_addon_price || 0));
        }
        this.updateTotal();
      }
    },
    resetCart() {
      this.$reset();
    },

    applyCoupon(coupon) {
      // Check if a coupon is already applied
      if (this.appliedCoupon) {
        throw new Error('A coupon is already applied. Please remove the current coupon first.');
      }

      // Validate coupon
      if (!coupon || !coupon.is_active) {
        throw new Error('Invalid or inactive coupon.');
      }

      // Check minimum order amount if specified
      if (coupon.min_order_value && this.TotalOrderPrice < parseFloat(coupon.min_order_value)) {
        throw new Error(`Minimum order amount of A$${coupon.min_order_value} required for this coupon.`);
      }

      this.appliedCoupon = coupon;
      this.calculateDiscount();
    },

    unapplyCoupon() {
      this.appliedCoupon = null;
      this.TotalDiscount = 0;
    },

    calculateDiscount() {
      if (!this.appliedCoupon) {
        this.TotalDiscount = 0;
        return;
      }

      const discountType = this.appliedCoupon.discount_type;
      const discountValue = parseFloat(this.appliedCoupon.discount_upto);

      switch (discountType) {
        case 'PERCENTAGE':{
          this.TotalDiscount = (discountValue / 100) * this.TotalOrderPrice;
          break;
        }
        case 'FIXED_ORDER':{
          this.TotalDiscount = discountValue;
          // Ensure that discount does not exceed the total order price
          if (this.TotalDiscount > this.TotalOrderPrice) {
            this.TotalDiscount = this.TotalOrderPrice;
          }
          break;
        }
        case 'FIXED_ITEM':{
          // Apply fixed discount per item quantity
          const totalItems = this.cart.reduce((sum, item) => sum + item.quantity, 0);
          this.TotalDiscount = discountValue * totalItems;
          // Ensure that discount does not exceed the total order price
          if (this.TotalDiscount > this.TotalOrderPrice) {
            this.TotalDiscount = this.TotalOrderPrice;
          }
          break;
        }
        default:{
          this.TotalDiscount = 0;
          break;
        }
      }
    },
  },
});

// Setup caching for the store
